import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon, ExternalLink } from 'lucide-react';
import { DiscoveryResponse } from '../data';
import { getVersions } from '../versionData';
interface DetailDrawerProps {
  row: DiscoveryResponse;
  onClose: () => void;
  onCompare?: (row: DiscoveryResponse) => void;
  onViewAllVersions?: (row: DiscoveryResponse) => void;
}
// Simple word-level diff function
function computeDiff(oldText: string, newText: string) {
  const oldWords = oldText.split(/\s+/);
  const newWords = newText.split(/\s+/);
  // Very naive diff for demonstration purposes
  // In a real app, use a proper diffing library like 'diff'
  const result: React.ReactNode[] = [];
  let i = 0;
  let j = 0;
  while (i < oldWords.length || j < newWords.length) {
    if (i < oldWords.length && j < newWords.length && oldWords[i] === newWords[j]) {
      result.push(<span key={`same-${i}-${j}`} data-id="element-94">{oldWords[i]} </span>);
      i++;
      j++;
    } else {
      // Find next match to resync
      let matchFound = false;
      for (let lookAhead = 1; lookAhead < 10 && !matchFound; lookAhead++) {
        if (i + lookAhead < oldWords.length && oldWords[i + lookAhead] === newWords[j]) {
          // Deletions
          for (let k = 0; k < lookAhead; k++) {
            result.push(<span key={`del-${i + k}`} className="bg-rose-900/30 text-rose-200 line-through px-0.5 rounded mx-0.5" data-id="element-95">
                {oldWords[i + k]}
              </span>);
            result.push(' ');
          }
          i += lookAhead;
          matchFound = true;
        } else if (j + lookAhead < newWords.length && oldWords[i] === newWords[j + lookAhead]) {
          // Additions
          for (let k = 0; k < lookAhead; k++) {
            result.push(<span key={`add-${j + k}`} className="bg-emerald-900/30 text-emerald-200 px-0.5 rounded mx-0.5" data-id="element-96">
                {newWords[j + k]}
              </span>);
            result.push(' ');
          }
          j += lookAhead;
          matchFound = true;
        }
      }
      if (!matchFound) {
        if (i < oldWords.length) {
          result.push(<span key={`del-fallback-${i}`} className="bg-rose-900/30 text-rose-200 line-through px-0.5 rounded mx-0.5" data-id="element-97">
              {oldWords[i]}
            </span>);
          result.push(' ');
          i++;
        }
        if (j < newWords.length) {
          result.push(<span key={`add-fallback-${j}`} className="bg-emerald-900/30 text-emerald-200 px-0.5 rounded mx-0.5" data-id="element-98">
              {newWords[j]}
            </span>);
          result.push(' ');
          j++;
        }
      }
    }
  }
  return result;
}
export function DetailDrawer({
  row,
  onClose,
  onCompare,
  onViewAllVersions
}: DetailDrawerProps) {
  const data = getVersions(row.id, row.response, row.party, row.set);
  const [versionIndex, setVersionIndex] = useState(data.versions.length - 1);
  const [showChanges, setShowChanges] = useState(false);
  const [objectionsOpen, setObjectionsOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [slideDirection, setSlideDirection] = useState(0); // 1 for right (newer), -1 for left (older)
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  // Reset state when row changes
  useEffect(() => {
    setVersionIndex(data.versions.length - 1);
    setShowChanges(false);
    setObjectionsOpen(false);
    setNotes('');
  }, [row.id, data.versions.length]);
  const handlePrevVersion = () => {
    if (versionIndex > 0) {
      setSlideDirection(-1);
      setVersionIndex(versionIndex - 1);
    }
  };
  const handleNextVersion = () => {
    if (versionIndex < data.versions.length - 1) {
      setSlideDirection(1);
      setVersionIndex(versionIndex + 1);
    }
  };
  const safeIndex = Math.min(versionIndex, data.versions.length - 1);
  const currentVersion = data.versions[safeIndex];
  const isLatest = safeIndex === data.versions.length - 1;
  const hasMultipleVersions = data.versions.length > 1;
  return <motion.div initial={{
    x: '100%'
  }} animate={{
    x: 0
  }} exit={{
    x: '100%'
  }} transition={{
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.3
  }} className="w-[40%] min-w-[400px] border-l border-geniac-border bg-geniac-bg shadow-[-8px_0_24px_rgba(0,0,0,0.3)] flex flex-col h-full z-20" data-id="element-99">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-geniac-border flex items-start justify-between" data-id="element-100">
        <div data-id="element-101">
          <h2 className="text-lg font-semibold text-geniac-text" data-id="element-102">
            {row.type === 'Form Interrogatories' ? 'Form Interrogatory' : row.type === 'Special Interrogatories' ? 'Special Interrogatory' : row.type === 'Requests for Admission' ? 'Request for Admission' : 'Document Request'}{' '}
            {row.number.replace(/^(Form|Special No\.|RFA No\.|Doc Req No\.)\s*/, '')}
          </h2>
          <p className="text-sm text-geniac-muted mt-1" data-id="element-103">
            {row.party} • {row.set}
          </p>
        </div>
        <button onClick={onClose} className="p-1.5 text-geniac-muted hover:text-geniac-text hover:bg-geniac-hover rounded-md transition-colors" data-id="element-104">
          <XIcon className="w-5 h-5" data-id="element-105" />
        </button>
      </div>

      {/* Pinned Question */}
      <div className="flex-shrink-0 px-6 py-5 bg-geniac-panel/30" data-id="element-106">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-107">
          Question
        </h3>
        <p className="text-[15px] leading-relaxed text-geniac-text" data-id="element-108">
          {row.question}
        </p>
      </div>
      <div className="h-px bg-geniac-border flex-shrink-0" data-id="element-109" />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6" data-id="element-110">
        {/* Response Section */}
        <section data-id="element-111">
          <div className="flex items-center justify-between mb-3" data-id="element-112">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-geniac-muted" data-id="element-113">
              Response
            </h3>

            {hasMultipleVersions && <div className="flex items-center gap-4" data-id="element-114">
                {onViewAllVersions && <button onClick={() => onViewAllVersions(row)} className="text-xs font-medium text-geniac-accent hover:text-geniac-accent/80 transition-colors" data-id="element-115">
                    View all versions &rarr;
                  </button>}
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-116">
                  <span className="text-xs text-geniac-muted" data-id="element-117">
                    Show changes
                  </span>
                  <div className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${showChanges ? 'bg-geniac-accent' : 'bg-geniac-border'}`} data-id="element-118">
                    <input type="checkbox" className="sr-only" checked={showChanges} onChange={e => setShowChanges(e.target.checked)} data-id="element-119" />
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${showChanges ? 'translate-x-3.5' : 'translate-x-0.5'}`} data-id="element-120" />
                  </div>
                </label>

                <div className="flex items-center gap-2 bg-geniac-panel rounded-md border border-geniac-border p-0.5" data-id="element-121">
                  <button onClick={handlePrevVersion} disabled={versionIndex === 0} className="p-1 text-geniac-muted hover:text-geniac-text disabled:opacity-30 disabled:hover:text-geniac-muted transition-colors" data-id="element-122">
                    <ChevronLeft className="w-4 h-4" data-id="element-123" />
                  </button>
                  <span className="text-xs font-medium text-geniac-text min-w-[4rem] text-center" data-id="element-124">
                    v{versionIndex + 1} of {data.versions.length}
                  </span>
                  <button onClick={handleNextVersion} disabled={isLatest} className="p-1 text-geniac-muted hover:text-geniac-text disabled:opacity-30 disabled:hover:text-geniac-muted transition-colors" data-id="element-125">
                    <ChevronRight className="w-4 h-4" data-id="element-126" />
                  </button>
                </div>
              </div>}
          </div>

          <div className="relative overflow-hidden rounded-lg border border-geniac-border bg-geniac-panel" data-id="element-127">
            {!isLatest && <div className="bg-amber-900/20 px-4 py-2 border-b border-amber-900/30 flex items-center justify-center" data-id="element-128">
                <span className="text-xs font-medium text-amber-400" data-id="element-129">
                  Superseded — {currentVersion.label}, {currentVersion.date}
                </span>
              </div>}
            {isLatest && hasMultipleVersions && <div className="bg-geniac-hover px-4 py-2 border-b border-geniac-border flex items-center justify-center" data-id="element-130">
                <span className="text-xs font-medium text-geniac-muted" data-id="element-131">
                  Current — {currentVersion.label}, {currentVersion.date}
                </span>
              </div>}

            <div className="p-4 relative min-h-[120px]" data-id="element-132">
              <AnimatePresence initial={false} mode="wait" custom={slideDirection} data-id="element-133">
                <motion.div key={versionIndex} custom={slideDirection} initial={{
                x: slideDirection * 20,
                opacity: 0
              }} animate={{
                x: 0,
                opacity: 1
              }} exit={{
                x: slideDirection * -20,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="text-[15px] leading-relaxed text-geniac-text whitespace-pre-wrap" data-id="element-134">
                  {showChanges && versionIndex > 0 ? computeDiff(data.versions[versionIndex - 1].text, currentVersion.text) : currentVersion.text}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* Objections Section */}
        <section className="border border-geniac-border rounded-lg bg-geniac-panel overflow-hidden" data-id="element-135">
          <button onClick={() => setObjectionsOpen(!objectionsOpen)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-geniac-hover transition-colors" data-id="element-136">
            <span className="text-sm font-medium text-geniac-text" data-id="element-137">
              Objections
            </span>
            {objectionsOpen ? <ChevronDown className="w-4 h-4 text-geniac-muted" data-id="element-138" /> : <ChevronRightIcon className="w-4 h-4 text-geniac-muted" data-id="element-139" />}
          </button>

          <AnimatePresence data-id="element-140">
            {objectionsOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden" data-id="element-141">
                <div className="px-4 pb-4 pt-1 border-t border-geniac-border/50" data-id="element-142">
                  <p className="text-sm leading-relaxed text-geniac-muted italic" data-id="element-143">
                    {data.objections}
                  </p>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>

        {/* Source Section */}
        <section data-id="element-144">
          <a href="#" className="inline-flex items-center gap-1.5 text-sm text-geniac-accent hover:text-geniac-accent/80 transition-colors group" data-id="element-145">
            <ExternalLink className="w-4 h-4" data-id="element-146" />
            <span className="group-hover:underline underline-offset-2" data-id="element-147">
              Source: {data.source}
            </span>
          </a>
        </section>

        {/* Notes Section */}
        <section className="pt-4 border-t border-geniac-border" data-id="element-148">
          <label htmlFor="notes" className="block text-xs font-semibold uppercase tracking-wider text-geniac-muted mb-2" data-id="element-149">
            Notes
          </label>
          <textarea id="notes" rows={4} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes for deposition prep..." className="w-full bg-geniac-panel border border-geniac-border rounded-lg px-3 py-2 text-sm text-geniac-text placeholder-geniac-muted focus:outline-none focus:ring-1 focus:ring-geniac-accent focus:border-geniac-accent resize-y mb-4" data-id="element-150" />

          {onCompare && <button onClick={() => onCompare(row)} className="flex items-center gap-1.5 text-sm font-medium text-geniac-muted hover:text-geniac-accent transition-colors" data-id="element-151">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-152">
                <rect x="3" y="3" width="7" height="18" rx="1" data-id="element-153" />
                <rect x="14" y="3" width="7" height="18" rx="1" data-id="element-154" />
              </svg>
              Compare across parties
            </button>}
        </section>
      </div>
    </motion.div>;
}